import pdfMake from "pdfmake/build/pdfmake.js";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import UtilMoney from "@/utils/UtilMoney.js";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export default class UtilOrdemDePagamentoPDF {
  static logoCache = null;

  static async carregarLogo() {
    if (this.logoCache) return this.logoCache;

    try {
      const response = await fetch("/img/logo-delta-ducon.png");
      if (!response.ok) return null;

      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.logoCache = reader.result;
          resolve(this.logoCache);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  }

  static texto(valor) {
    if (valor === null || valor === undefined) return "";
    return String(valor).trim();
  }

  static primeiroTexto(...valores) {
    for (const valor of valores) {
      const texto = this.texto(valor);
      if (texto) return texto;
    }
    return "";
  }

  static formatarData(valor) {
    if (!valor) return "";

    if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
      return valor.toLocaleDateString("pt-BR");
    }

    const texto = this.texto(valor);
    if (!texto) return "";

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
      return texto;
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(texto)) {
      const [ano, mes, dia] = texto.slice(0, 10).split("-");
      return `${dia}/${mes}/${ano}`;
    }

    const data = new Date(texto);
    if (!Number.isNaN(data.getTime())) {
      return data.toLocaleDateString("pt-BR");
    }

    return texto;
  }

  static formatarCnpj(valor) {
    const numeros = this.texto(valor).replace(/\D+/g, "");
    if (numeros.length !== 14) return this.texto(valor);
    return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  }

  static limparEspacos(valor) {
    return this.texto(valor).replace(/\s+/g, " ").trim();
  }

  static valorOuTraco(valor) {
    return this.texto(valor) || "-";
  }

  static dataOuMarcador(valor) {
    return this.formatarData(valor) || "/  /";
  }

  static pareceUuid(valor) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(this.texto(valor));
  }

  static formatarComDigito(valor, digito) {
    const principal = this.texto(valor);
    const sufixo = this.texto(digito);

    if (!principal) return "";
    return sufixo ? `${principal}-${sufixo}` : principal;
  }

  static identificarBancoDescricao(banco) {
    if (banco && typeof banco === "object") {
      const codigo = this.primeiroTexto(banco.bancoCodigo, banco.codigo, banco.code);
      const nome = this.primeiroTexto(banco.bancoNome, banco.nome, banco.name, banco.descricao, banco.description);

      if (codigo && nome) return `${codigo} - ${nome}`;
      return this.primeiroTexto(nome, codigo);
    }

    const textoBanco = this.texto(banco);
    if (!textoBanco || this.pareceUuid(textoBanco)) return "";
    return textoBanco;
  }

  static identificarDadosBancarios(dado = {}) {
    if (dado?.tipoCredor === "Fornecedor") {
      const fornecedor = dado?.credorFornecedor || {};
      return {
        banco: fornecedor.banco,
        agencia: fornecedor.agencia,
        agenciaDigito: this.primeiroTexto(fornecedor.agenciaDigito, fornecedor.digitoAgencia),
        conta: fornecedor.contaBancaria,
        contaDigito: this.primeiroTexto(fornecedor.contaBancariaDigito, fornecedor.digitoContaBancaria),
        contaTipo: fornecedor.contaBancariaTipo,
      };
    }

    const colaborador = dado?.credorColaborador || {};
    return {
      banco: colaborador.banco,
      agencia: colaborador.agencia,
      agenciaDigito: colaborador.digitoAgencia,
      conta: colaborador.contaBancaria,
      contaDigito: colaborador.digitoContaBancaria,
      contaTipo: colaborador.contaBancariaTipo,
    };
  }

  static identificarRotuloConta(contaTipo) {
    const tipo = this.texto(contaTipo).toUpperCase();

    if (["C_P", "CP", "POUPANCA", "CONTA POUPANCA"].includes(tipo)) {
      return "C/P.";
    }

    return "C/C.";
  }

  static montarDescricaoPagamento(dado = {}) {
    const formaPagamento = this.primeiroTexto(dado?.formaDePagamento);
    if (!formaPagamento) return "";
    if (/boleto/i.test(formaPagamento)) return formaPagamento;

    const dadosBancarios = this.identificarDadosBancarios(dado);
    const banco = this.identificarBancoDescricao(dadosBancarios.banco);
    const agencia = this.formatarComDigito(dadosBancarios.agencia, dadosBancarios.agenciaDigito);
    const conta = this.formatarComDigito(dadosBancarios.conta, dadosBancarios.contaDigito);
    const rotuloConta = this.identificarRotuloConta(dadosBancarios.contaTipo);
    const detalhes = [];

    if (banco) detalhes.push(`Banco = ${banco}`);
    if (agencia) detalhes.push(`Ag. = ${agencia}`);
    if (conta) detalhes.push(`${rotuloConta} = ${conta}`);

    if (detalhes.length === 0) return formaPagamento;
    return `${formaPagamento} ${detalhes.join("  ")}`;
  }

  static identificarCredor(dado = {}) {
    if (dado?.tipoCredor === "Fornecedor") {
      const fornecedor = dado?.credorFornecedor || {};
      return {
        nome: this.primeiroTexto(fornecedor.nomeFantasia, fornecedor.razaoSocial, fornecedor.nome),
        documento: this.primeiroTexto(fornecedor.cnpj, fornecedor.cpf),
      };
    }

    const colaborador = dado?.credorColaborador || {};
    return {
      nome: this.primeiroTexto(colaborador.nome, colaborador.name),
      documento: this.primeiroTexto(colaborador.cpf),
    };
  }

  static identificarEmitente(dado = {}) {
    return this.primeiroTexto(dado?.emitente?.nome, dado?.emitente?.name);
  }

  static identificarRequisitante(dado = {}) {
    return this.primeiroTexto(dado?.requisitante?.nome, dado?.requisitante?.name);
  }

  static identificarJob(dado = {}) {
    return this.primeiroTexto(dado?.job?.codigo, dado?.job?.code, dado?.job?.numero, dado?.job?.descricao);
  }

  static identificarCentroCusto(dado = {}) {
    // Tenta extrair do colaborador (costCenterId pode vir como objeto ou como string/ID)
    if (dado?.tipoCredor === "Colaborador") {
      const cc = dado?.credorColaborador?.costCenterId;
      if (cc && typeof cc === "object") {
        return this.primeiroTexto(cc.description, cc.sigla, cc.code, cc.codigo, cc.descricao, cc.nome, cc.name);
      }
      if (cc && typeof cc === "string") {
        return cc;
      }
    }

    // Campos convencionais da OP
    return this.primeiroTexto(
      dado?.centroDeCusto,
      dado?.centroCusto,
      dado?.job?.centroDeCusto,
      dado?.job?.centroCusto,
      dado?.ordemDeServico?.centroDeCusto,
      dado?.ordemDeServico?.centroCusto
    );
  }

  static identificarTipoDespesa(dado = {}) {
    return this.primeiroTexto(
      dado?.tipo,
      dado?.tipoDescricao,
      dado?.tipoDeDespesa,
      dado?.ordemDeServico?.tipo,
      dado?.despesas?.[0]?.despesa?.grupo,
      dado?.despesas?.[0]?.despesa?.descricao
    );
  }

  static linhasDespesas(despesas = []) {
    const lista = Array.isArray(despesas) ? despesas : [];

    return lista.map((despesa, index) => {
      const numero = this.primeiroTexto(despesa?.numero, index + 1);
      const data = this.formatarData(despesa?.dataDaDespesa || despesa?.dataCriacao);
      const referencia = this.primeiroTexto(
        despesa?.despesa?.descricao,
        despesa?.despesa?.nome,
        despesa?.despesa?.codigo,
        despesa?.despesa?.grupo,
        despesa?.observacao
      );
      const referenciaComplementar = this.primeiroTexto(
        despesa?.despesa?.descricaoCompleta,
        despesa?.observacao
      );
      const observacao = this.primeiroTexto(
        despesa?.observacao,
        despesa?.despesa?.descricao,
        despesa?.despesa?.nome
      );
      const valor = UtilMoney.toR$BRL(despesa?.valor);

      return [
        { text: numero, alignment: "center", fontSize: 8 },
        { text: this.valorOuTraco(data), alignment: "center", fontSize: 8 },
        {
          stack: [
            { text: this.valorOuTraco(this.limparEspacos(referencia)), fontSize: 8 },
            referenciaComplementar && this.limparEspacos(referenciaComplementar) !== this.limparEspacos(referencia)
              ? { text: this.limparEspacos(referenciaComplementar), fontSize: 7, color: "#444", margin: [0, 2, 0, 0] }
              : { text: "" }
          ]
        },
        {
          text: this.valorOuTraco(this.limparEspacos(observacao)),
          fontSize: 8
        },
        { text: valor, alignment: "right", fontSize: 8 }
      ];
    });
  }

  static assinaturas(dado = {}) {
    const emitente = this.identificarEmitente(dado);
    const requisitante = this.identificarRequisitante(dado);
    const aprovadores = Array.isArray(dado?.aprovadores) ? dado.aprovadores : [];

    const aprovadorPorTipo = (tipo) => {
      return aprovadores.find((item) => item?.aprovadorTipo === tipo) || null;
    };

    const nomeAprovador = (aprovador) => {
      if (!aprovador) return "";
      return this.primeiroTexto(aprovador?.aprovador?.nome, aprovador?.aprovador?.name, aprovador?.aprovador?.descricao, aprovador?.aprovador?.codigo);
    };

    const dataAprovador = (aprovador) => {
      if (!aprovador) return "/  /";
      return this.dataOuMarcador(aprovador?.dataDecisao);
    };

    const requisitanteAprovador = aprovadorPorTipo("Requisitante");
    const coordenadorAprovador = aprovadorPorTipo("Coordenador");
    const gestorAprovador = aprovadorPorTipo("Gestor");
    const financeiroAprovador = aprovadorPorTipo("Financeiro");

    return [
      [
        { text: "Emitente:", bold: true, fontSize: 8 },
        { text: emitente, fontSize: 8 },
        { text: "Assinado eletrônicamente em:", bold: true, fontSize: 8 },
        { text: this.dataOuMarcador(dado?.dataAprovacaoPagamento || dado?.dataCriacao), fontSize: 8 }
      ],
      [
        { text: "Requisitante:", bold: true, fontSize: 8 },
        { text: nomeAprovador(requisitanteAprovador) || requisitante, fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: dataAprovador(requisitanteAprovador) || this.dataOuMarcador(dado?.dataCriacao), fontSize: 8 }
      ],
      [
        { text: "Coordenador:", bold: true, fontSize: 8 },
        { text: nomeAprovador(coordenadorAprovador), fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: dataAprovador(coordenadorAprovador), fontSize: 8 }
      ],
      [
        { text: "Aprovação Sup:", bold: true, fontSize: 8 },
        { text: nomeAprovador(gestorAprovador), fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: dataAprovador(gestorAprovador), fontSize: 8 }
      ],
      [
        { text: "Financeiro:", bold: true, fontSize: 8 },
        { text: nomeAprovador(financeiroAprovador), fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: dataAprovador(financeiroAprovador), fontSize: 8 }
      ]
    ];
  }

  static criarDefinicao(dado = {}, logo = null) {
    const numero = this.primeiroTexto(dado?.numero, dado?.code, dado?.codigo);
    const emissao = this.formatarData(dado?.dataCriacao || dado?.createdAt || new Date());
    const previsaoPagamento = this.formatarData(dado?.dataPrevisaoPagamento || dado?.parcelas?.[0]?.dataDoPagamento);
    const credor = this.identificarCredor(dado);
    const tipoDaOP = this.identificarTipoDespesa(dado);
    const centroCusto = this.identificarCentroCusto(dado);
    const job = this.identificarJob(dado);
    const numeroNF = this.primeiroTexto(dado?.notaFiscal, dado?.numeroNotaFiscal, dado?.numeroNF, dado?.nf);
    const despesas = Array.isArray(dado?.despesas) ? dado.despesas : [];
    const linhas = this.linhasDespesas(despesas);
    const total = UtilMoney.toR$BRL(dado?.valorTotal || despesas.reduce((acc, item) => acc + Number(item?.valor || 0), 0));
    const subtotais = UtilMoney.toR$BRL(despesas.reduce((acc, item) => acc + Number(item?.valor || 0), 0));

    const bodyDespesas = linhas.length > 0
      ? linhas
      : [[
        { text: "", fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: "", fontSize: 8 },
        { text: "", fontSize: 8 }
      ]];

    return {
      pageSize: "A4",
      pageMargins: [26, 20, 26, 20],
      defaultStyle: {
        fontSize: 9,
        color: "#111"
      },
      styles: {
        label: {
          bold: true,
          fontSize: 9
        },
        pequeno: {
          fontSize: 8
        }
      },
      content: [
        {
          columns: [
            logo ? { image: logo, width: 110, margin: [0, 8, 0, 0] } : { text: "" },
            {
              width: "*",
              table: {
                widths: ["62%", "38%"],
                body: [
                  [{ text: "Número da OP:", bold: true, fontSize: 12, alignment: "right" }, { text: numero, bold: true, fontSize: 12, alignment: "right" }],
                  [{ text: "Emissão:", alignment: "right" }, { text: this.valorOuTraco(emissao), alignment: "right" }],
                  [{ text: "Previsão de Pagamento:", alignment: "right" }, { text: this.valorOuTraco(previsaoPagamento), alignment: "right" }]
                ]
              },
              layout: "noBorders"
            }
          ],
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            widths: ["16%", "84%"],
            body: [
              [
                { text: "Credor:", style: "label" },
                { text: this.valorOuTraco(credor.nome) }
              ],
              [
                { text: "CPF/CNPJ:", style: "label" },
                { text: this.valorOuTraco(this.formatarCnpj(credor.documento)) }
              ],
              [
                { text: "Pagamento:", style: "label" },
                { text: this.valorOuTraco(this.montarDescricaoPagamento(dado)) }
              ]
            ]
          },
          layout: {
            hLineWidth: () => 0.8,
            vLineWidth: () => 0.8,
            hLineColor: () => "#222",
            vLineColor: () => "#222",
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 5,
            paddingBottom: () => 5
          },
          margin: [0, 0, 0, 8]
        },
        {
          table: {
            widths: ["16%", "34%", "14%", "36%"],
            body: [
              [
                { text: "Tipo da OP:", style: "label" },
                { text: this.valorOuTraco(this.primeiroTexto(tipoDaOP)), colSpan: 1 },
                { text: "Centro de Custo:", style: "label" },
                { text: this.valorOuTraco(this.primeiroTexto(centroCusto)) }
              ],
              [
                { text: "Despesa Geral", style: "pequeno" },
                { text: this.valorOuTraco(this.primeiroTexto(dado?.tipo, dado?.tipoDescricao)), style: "pequeno" },
                { text: "Job:", style: "label" },
                { text: this.valorOuTraco(this.primeiroTexto(job)), style: "pequeno" }
              ],
              [
                { text: "Nº NF:", style: "label" },
                { text: this.valorOuTraco(numeroNF), style: "pequeno" },
                { text: "", style: "pequeno" },
                { text: "", style: "pequeno" }
              ]
            ]
          },
          layout: {
            hLineWidth: () => 0.8,
            vLineWidth: () => 0.8,
            hLineColor: () => "#222",
            vLineColor: () => "#222",
            paddingLeft: () => 6,
            paddingRight: () => 6,
            paddingTop: () => 5,
            paddingBottom: () => 5
          },
          margin: [0, 0, 0, 14]
        },
        {
          table: {
            widths: ["6%", "12%", "36%", "32%", "14%"],
            headerRows: 1,
            body: [
              [
                { text: "It.", bold: true, alignment: "center", fontSize: 8 },
                { text: "Data", bold: true, alignment: "center", fontSize: 8 },
                { text: "Referência/Despesa", bold: true, alignment: "center", fontSize: 8 },
                { text: "Observação", bold: true, alignment: "center", fontSize: 8 },
                { text: "Valor Despesa", bold: true, alignment: "right", fontSize: 8 }
              ],
              ...bodyDespesas.map((linha) => (Array.isArray(linha) ? linha : [linha[0], linha[1], linha[2], linha[3], linha[4]])),
              [
                { text: "", colSpan: 4 },
                { text: "" },
                { text: "" },
                { text: "" },
                { text: subtotais, bold: true, alignment: "right", fontSize: 8 }
              ],
              [
                { text: "", colSpan: 4 },
                { text: "" },
                { text: "" },
                { text: "" },
                { text: total, bold: true, alignment: "right", fontSize: 8, fillColor: "#d9d9d9" }
              ]
            ]
          },
          layout: {
            hLineWidth: (index, node) => {
              const isHeaderRow = index === 0;
              const isSubTotalRow = index === node.table.body.length - 2;
              if (isHeaderRow) return 0.8;
              if (isSubTotalRow) return 0.5;
              return 0.35;
            },
            vLineWidth: () => 0.45,
            hLineColor: () => "#d0d0d0",
            vLineColor: () => "#d0d0d0",
            fillColor: (rowIndex, node) => {
              if (rowIndex === 0) return "#eaeaea";
              if (rowIndex === node.table.body.length - 1) return "#d9d9d9";
              return null;
            },
            paddingLeft: () => 3,
            paddingRight: () => 3,
            paddingTop: () => 2,
            paddingBottom: () => 2
          },
          margin: [0, 0, 0, 80]
        },
        {
          canvas: [{ type: "line", x1: 0, y1: 0, x2: 543, y2: 0, lineWidth: 0.8, lineColor: "#222" }],
          margin: [0, 0, 0, 8]
        },
        {
          table: {
            widths: ["16%", "46%", "24%", "14%"],
            body: this.assinaturas(dado)
          },
          layout: "noBorders",
          margin: [0, 14, 0, 0]
        }
      ],
      footer: (currentPage, pageCount) => ({
        margin: [28, 0, 28, 0],
        columns: [
          { text: `Impresso dia ${this.formatarData(new Date())} às ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} pag. ${currentPage} de ${pageCount}`, fontSize: 8, color: "#444", alignment: "right" }
        ]
      })
    };
  }

  static async gerarPDF(dado = {}) {
    const logo = await this.carregarLogo();
    const definicao = this.criarDefinicao(dado, logo);
    const numero = this.texto(dado?.numero || dado?.code || dado?.codigo || "op");
    pdfMake.createPdf(definicao).download(`Ordem_Pagamento_${numero}.pdf`);
  }
}
